import { useCallback, useEffect, useRef, useState } from 'react';
import useDebounce from '../../../components/Shared/Delay/index.tsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { ConstantActions } from '../../../Services/_actions';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import {
  templateFormAtom,
  templateFormButtonsAtom,
  templateFormErrorsAtom,
  templateListRefreshAtom,
} from '../state/templateAtoms';
import { templateService } from '../services/templateService';
import { TEMPLATE_BASE_PATH } from '../constants/templateApi';
import {
  DEFAULT_TEMPLATE_BUTTONS,
  DEFAULT_TEMPLATE_FORM,
  HeaderOptions,
} from '../utils/templateConstants';
import {
  validateTemplateForm,
  hasValidationErrors,
  slugifyTemplateName,
  getBodyVariablePlacementError,
  isBodyVariablePlacementError,
  TEMPLATE_NAME_TAKEN_MESSAGE,
} from '../utils/templateValidation';
import APIService from '../../../Services/APIService';
import { Apis } from '../../../Services/app.constants';
import {
  buildCreateTemplatePayload,
  buildCloneFormValues,
  getOrganisationNameFromTemplate,
  normalizeTemplate,
  mapOrganisationToSelectOption,
} from '../utils/normalizeTemplate';
import { validateHeaderFile, getHeaderAcceptValue } from '../utils/templateLimitUtils';
import { extractOrganisationList } from '../adapters/organisationListAdapter';
import { isTemplateArchived } from '../utils/templateArchiveUtils';
import { isRejectedTemplate } from '../utils/getRejectionReason';
import { parseApiError } from '../utils/templateErrorUtils';
import { mapTagOption } from './useTemplateTags';

function extractBotNumberList(data) {
  if (Array.isArray(data)) return data;
  return data?.list ?? data?.results ?? [];
}

function extractButtonsFromComponents(components) {
  const list = Array.isArray(components) ? components : [];
  const buttonArr = list.find((c) => c.type === 'BUTTONS')?.buttons ?? [];
  const result = { callAction: [], quickReply: [] };
  buttonArr.forEach((button) => {
    if (button.type === 'QUICK_REPLY') result.quickReply.push(button);
    else result.callAction.push(button);
  });
  return result;
}

export default function useTemplateForm({ templateName, botNumber, cloneState } = {}) {
  const isEditMode = Boolean(templateName && botNumber);
  const navigate = useNavigate();
  const location = useLocation();
  const clonePayload = cloneState ?? location.state?.cloneFrom ?? null;
  const cloneSourceName = location.state?.sourceName ?? clonePayload?.name ?? '';
  const isCloneMode = Boolean(!isEditMode && clonePayload);
  const { showSnackBar } = useSnackBar();
  const constantActions = ConstantActions();
  const loggedInUser = useRecoilValue(userInformationAtom);

  const [formData, setFormData] = useRecoilState(templateFormAtom);
  const [buttonArrayObj, setButtonArrayObj] = useRecoilState(templateFormButtonsAtom);
  const [formErrors, setFormErrors] = useRecoilState(templateFormErrorsAtom);
  const setListRefresh = useSetRecoilState(templateListRefreshAtom);

  const resetForm = useResetRecoilState(templateFormAtom);
  const resetButtons = useResetRecoilState(templateFormButtonsAtom);
  const resetErrors = useResetRecoilState(templateFormErrorsAtom);

  const bodyEditorRef = useRef(null);
  const [file, setFile] = useState(null);
  const [existingFileUrl, setExistingFileUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [filterObj, setFilterObj] = useState({ botNumber: '', organisation: '' });
  const [selectedTags, setSelectedTags] = useState([]);
  const [contextErrors, setContextErrors] = useState({ organisation: '', botNumber: '' });
  const [botOptionsCount, setBotOptionsCount] = useState(0);
  const [isCheckingTemplateName, setIsCheckingTemplateName] = useState(false);

  const debouncedTemplateName = useDebounce(formData?.name, 450);
  const nameCheckRequestIdRef = useRef(0);

  const isSuperuser = Boolean(loggedInUser?.is_superuser);

  const clearFieldError = useCallback(
    (key) => {
      setFormErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [setFormErrors]
  );

  const getHeaderType = useCallback(
    (type) => {
      const components = Array.isArray(formData?.components) ? formData.components : [];
      const header = components.find((c) => c?.type === 'HEADER')?.format;
      return header ? type === header : false;
    },
    [formData?.components]
  );

  const getHeaderText = useCallback(() => {
    const components = Array.isArray(formData?.components) ? formData.components : [];
    const headerObj = components.find((c) => c?.type === 'HEADER');
    return headerObj?.text || '';
  }, [formData?.components]);

  const fetchChannels = useCallback(
    async (organisation) => {
      try {
        const res = await templateService.getBotNumbers(organisation?.organisation_id);
        if (res?.status === 200) {
          const channels = extractBotNumberList(res?.data);
          setBotOptionsCount(channels.length);
          const errorMsg = res?.data?.data?.general_error?.error?.error_user_msg;


          if (channels.length === 1) {
            setFilterObj((prev) => ({
              ...prev,
              organisation: organisation?.organisation_id,
              organisationObj: organisation,
              botNumber: channels[0]?.wa_phone_number,
              botNumberObj: channels[0],
            }));
          } else if (channels.length === 0) {
            setFilterObj((prev) => ({
              ...prev,
              organisation: organisation?.organisation_id,
              organisationObj: organisation,
              botNumber: '',
              botNumberObj: null,
            }));
            showSnackBar(errorMsg || 'Bot number is not available for this organisation', 'error');
          } else {
            setFilterObj((prev) => ({
              ...prev,
              organisation: organisation?.organisation_id,
              organisationObj: organisation,
              botNumber: '',
              botNumberObj: null,
            }));
          }
        } else {
          const errorMsg = res?.data?.data?.general_error?.error?.error_user_msg;
          showSnackBar(errorMsg || 'Unexpected response structure or status', 'error');
        }
      } catch (error) {
        const errorMsg = error?.response?.data?.data?.general_error?.error?.error_user_msg;
        showSnackBar(errorMsg || 'Failed to fetch bot numbers', 'error');
      }
    },
    [showSnackBar]
  );

  const handleOrganisationChange = useCallback(
    (organisation) => {
      setFilterObj((prev) => ({
        ...prev,
        organisation: organisation?.organisation_id ?? '',
        organisationObj: organisation ?? null,
        botNumber: '',
        botNumberObj: null,
      }));
      setBotOptionsCount(0);
      setContextErrors({ organisation: '', botNumber: '' });
      setSelectedTags([]);
      if (organisation) {
        fetchChannels(organisation);
      }
    },
    [fetchChannels]
  );

  const handleBotChange = useCallback((botNumber) => {
    setFilterObj((prev) => ({
      ...prev,
      botNumber: botNumber?.wa_phone_number ?? '',
      botNumberObj: botNumber ?? null,
    }));
    setContextErrors((prev) => ({ ...prev, botNumber: '' }));
  }, []);

  const fetchOrganisations = useCallback(async () => {
    if (!loggedInUser?.is_superuser) {
      const organisation = loggedInUser?.profile?.organisation;
      if (organisation) {
        setFilterObj((prev) => ({
          ...prev,
          organisation: organisation.organisation_id,
          organisationObj: organisation,
        }));
        await fetchChannels(organisation);
      }
      return;
    }

    try {
      const res = await constantActions.getOrganisationsWithPagination({ page: 0, search: '' });
      if (res?.status === 200 && res?.data) {
        const list = extractOrganisationList(res.data);

        if (list.length === 1) {
          setFilterObj((prev) => ({
            ...prev,
            organisation: list[0]?.organisation_id,
            organisationObj: list[0],
          }));
          await fetchChannels(list[0]);
        }
      } else {
        const errorMsg = res?.data?.data?.general_error?.error?.error_user_msg;

        showSnackBar(errorMsg || 'Unexpected response status or data', 'error');
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.data?.general_error?.error?.error_user_msg;
      showSnackBar(errorMsg || 'Failed to fetch organizations', 'error');
    }
  }, [constantActions, fetchChannels, loggedInUser, showSnackBar]);

  const loadTemplate = useCallback(async () => {
    try {
      const res = await templateService.getTemplate(templateName, botNumber);
      if (res?.status === 200) {
        if (!isRejectedTemplate(res.data)) {
          showSnackBar('Only rejected templates can be edited', 'warning');
          navigate(`${TEMPLATE_BASE_PATH}/template-list`);
          return;
        }
        if (isTemplateArchived(res.data)) {
          showSnackBar('Archived templates cannot be edited', 'warning');
          navigate(`${TEMPLATE_BASE_PATH}/template-list`);
          return;
        }
        const normalized = normalizeTemplate(res.data);
        setFormData({ ...DEFAULT_TEMPLATE_FORM, ...normalized });
        setSelectedTags((normalized.tags ?? []).map(mapTagOption).filter(Boolean));
        setButtonArrayObj(extractButtonsFromComponents(normalized.components));
        setFilterObj((prev) => ({
          ...prev,
          organisation: res.data?.company_id ?? prev.organisation,
          botNumber: res.data?.bot_number,
          botNumberObj: {
            app_name: res.data?.app_name || '',
            wa_phone_number: res.data?.bot_number,
          },
        }));
        if (res.data?.media?.header) {
          setExistingFileUrl(res.data.media.header);
        }
        const loadedBodyText = normalized.components?.find((c) => c.type === 'BODY')?.text || '';
        const placementError = getBodyVariablePlacementError(loadedBodyText);
        if (placementError) {
          setFormErrors((prev) => ({ ...prev, body: placementError }));
        }
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.data?.general_error?.error?.error_user_msg;
      showSnackBar(errorMsg || 'Failed to load template', 'error');
    }
  }, [botNumber, navigate, setButtonArrayObj, setFormData, setFormErrors, showSnackBar, templateName]);

  const resolveCloneOrganisationOption = useCallback(async (payload) => {
    const companyId = payload?.company_id;
    if (!companyId) return null;

    const embeddedName = getOrganisationNameFromTemplate(payload);
    if (embeddedName) {
      return mapOrganisationToSelectOption({
        organisation_id: companyId,
        organisation_name: embeddedName,
      });
    }

    const loggedInOrg = loggedInUser?.profile?.organisation;
    if (
      loggedInOrg
      && String(loggedInOrg.organisation_id) === String(companyId)
    ) {
      return mapOrganisationToSelectOption(loggedInOrg);
    }

    try {
      const res = await APIService.get(`${Apis.Get_Organisation_List}${companyId}/`);
      const orgData = res?.data?.data ?? res?.data;
      if (res?.status === 200 && orgData?.organisation_id) {
        return mapOrganisationToSelectOption(orgData);
      }
    } catch {
      // Fall back to id-only option below.
    }

    return mapOrganisationToSelectOption({ organisation_id: companyId });
  }, [loggedInUser]);

  const loadCloneTemplate = useCallback(async () => {
    const normalized = normalizeTemplate(clonePayload);
    const cloneValues = buildCloneFormValues(normalized);
    setFormData({ ...DEFAULT_TEMPLATE_FORM, ...normalized, ...cloneValues });
    setSelectedTags((normalized.tags ?? []).map(mapTagOption).filter(Boolean));
    setButtonArrayObj(extractButtonsFromComponents(cloneValues.components));
    if (clonePayload?.media?.header) {
      setExistingFileUrl(clonePayload.media.header);
    }

    const orgOption = await resolveCloneOrganisationOption(clonePayload);
    setFilterObj((prev) => ({
      ...prev,
      organisation: clonePayload?.company_id ?? prev.organisation,
      organisationObj: orgOption ?? prev.organisationObj,
      botNumber: clonePayload?.bot_number ?? '',
      botNumberObj: clonePayload?.bot_number
        ? {
          app_name: clonePayload?.app_name || '',
          wa_phone_number: clonePayload.bot_number,
        }
        : null,
    }));

    const loadedBodyText = cloneValues.components?.find((c) => c.type === 'BODY')?.text || '';
    const placementError = getBodyVariablePlacementError(loadedBodyText);
    if (placementError) {
      setFormErrors((prev) => ({ ...prev, body: placementError }));
    }
  }, [clonePayload, resolveCloneOrganisationOption, setButtonArrayObj, setFormData, setFormErrors]);

  useEffect(() => {
    if (isEditMode) {
      loadTemplate();
      return;
    }
    if (isCloneMode) {
      loadCloneTemplate();
      return;
    }
    fetchOrganisations();
    resetButtons();
    resetForm();
    resetErrors();
    setSelectedTags([]);
  }, []);

  const handleChangeName = useCallback(
    (value) => {
      setFormData((prev) => ({ ...prev, name: slugifyTemplateName(value) }));
      clearFieldError('name');
    },
    [clearFieldError, setFormData]
  );

  useEffect(() => {
    if (isEditMode) {
      setIsCheckingTemplateName(false);
      return undefined;
    }

    const templateName = debouncedTemplateName?.trim();
    const botNumber = filterObj?.botNumber;

    if (!templateName || !botNumber) {
      setIsCheckingTemplateName(false);
      return undefined;
    }

    const requestId = nameCheckRequestIdRef.current + 1;
    nameCheckRequestIdRef.current = requestId;
    setIsCheckingTemplateName(true);

    let cancelled = false;
    (async () => {
      try {
        const result = await templateService.checkTemplateNameAvailability(
          templateName,
          botNumber
        );
        if (cancelled || requestId !== nameCheckRequestIdRef.current) {
          return;
        }

        if (!result.available) {
          setFormErrors((prev) => ({
            ...prev,
            name: result.message || TEMPLATE_NAME_TAKEN_MESSAGE,
          }));
        } else {
          clearFieldError('name');
        }
      } catch {
        if (!cancelled && requestId === nameCheckRequestIdRef.current) {
          clearFieldError('name');
        }
      } finally {
        if (!cancelled && requestId === nameCheckRequestIdRef.current) {
          setIsCheckingTemplateName(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    debouncedTemplateName,
    filterObj?.botNumber,
    isEditMode,
    clearFieldError,
    setFormErrors,
  ]);

  const handleChangeDescription = useCallback(
    (value) => {
      setFormData((prev) => ({ ...prev, description: value }));
    },
    [setFormData]
  );

  const onSelectHeaderType = useCallback(
    (obj) => {
      const nextType = obj?.value;
      const currentType = formData?.components?.find((c) => c.type === 'HEADER')?.format;
      if (nextType === currentType) return;

      setFile(null);
      setExistingFileUrl(null);
      clearFieldError('headerFile');
      clearFieldError('headerText');

      setFormData((prev) => {
        const isHeaderObj = prev.components?.find((c) => c.type === 'HEADER');
        const nextComponents = isHeaderObj
          ? prev.components.map((c) => {
              if (c.type !== 'HEADER') return c;
              const next = { ...c, format: nextType };
              if (nextType !== 'TEXT') delete next.text;
              return next;
            })
          : [...(prev.components || []), { type: 'HEADER', format: nextType }];

        return {
          ...prev,
          media: null,
          components: nextComponents,
        };
      });
    },
    [clearFieldError, formData?.components, setFormData]
  );

  const onFileSelect = useCallback(
    (e) => {
      const selected = e.target.files?.[0];
      if (!selected) return;
      const validation = validateHeaderFile(selected);
      if (!validation.valid) {
        // const errorMsg = res?.data?.data?.general_error?.error?.error_user_msg;
        showSnackBar(validation.message, 'error');
        return;
      }
      setFile(selected);
      setExistingFileUrl(null);
      clearFieldError('headerFile');
    },
    [clearFieldError, showSnackBar]
  );

  const onFileRemove = useCallback(() => {
    setFile(null);
    setExistingFileUrl(null);
    setFormData((prev) => ({ ...prev, media: null }));
  }, [setFormData]);

  const syncBodyValidationError = useCallback(
    (bodyText) => {
      const placementError = getBodyVariablePlacementError(bodyText);
      setFormErrors((prev) => {
        const next = { ...prev };
        if (placementError) {
          next.body = placementError;
          return next;
        }
        if (isBodyVariablePlacementError(next.body)) {
          delete next.body;
        }
        return next;
      });
    },
    [setFormErrors]
  );

  useEffect(() => {
    const bodyText = formData?.components?.find((c) => c.type === 'BODY')?.text || '';
    syncBodyValidationError(bodyText);
  }, [formData?.components, syncBodyValidationError]);

  const handleChangeBodyData = useCallback(
    (text) => {
      let nextBodyText = text;
      setFormData((prev) => {
        const variableRegex = /\{\{\d+\}\}/g;
        const matches = text.match(variableRegex) || [];
        const variables = matches.map((_, index) => `{{${index + 1}}}`);
        let newText = text;
        matches.forEach((match, index) => {
          newText = newText.replace(match, `{{${index + 1}}}`);
        });
        nextBodyText = newText;

        let bodyComponent = prev.components?.find((c) => c.type === 'BODY');
        const components = [...(prev.components || [])];
        if (!bodyComponent) {
          bodyComponent = { type: 'BODY', text: '' };
          components.push(bodyComponent);
        }

        const updatedVariables = variables.map(
          (_, index) => bodyComponent?.example?.body_text?.[0]?.[index] || ''
        );

        let parameters = [...(prev.param || [])];
        if (updatedVariables.length > 0) {
          while (parameters.length < variables.length) parameters.push('');
          if (parameters.length > variables.length) {
            parameters = parameters.slice(0, variables.length);
          }
          bodyComponent = {
            ...bodyComponent,
            text: newText,
            example: { body_text: [updatedVariables] },
          };
        } else {
          bodyComponent = { ...bodyComponent, text: newText };
        }

        return {
          ...prev,
          param: parameters,
          components: components.map((c) => (c.type === 'BODY' ? bodyComponent : c)),
        };
      });
      syncBodyValidationError(nextBodyText);
    },
    [setFormData, syncBodyValidationError]
  );

  const addBodyVariables = useCallback(() => {
    const bodyObj = formData?.components?.find((c) => c.type === 'BODY');
    const bodyText = bodyObj?.text || '';
    const cursorPosition = bodyEditorRef.current?.getSelectionStart?.() ?? bodyText.length;
    const variableCount = bodyObj?.example?.body_text?.[0]?.length || 0;
    const newVariable = `{{${variableCount + 1}}}`;
    const newText =
      bodyText.slice(0, cursorPosition) + newVariable + bodyText.slice(cursorPosition);
    handleChangeBodyData(newText);
    setTimeout(() => {
      bodyEditorRef.current?.setSelectionRange?.(
        cursorPosition + newVariable.length,
        cursorPosition + newVariable.length
      );
    }, 0);
  }, [formData?.components, handleChangeBodyData]);

  const removeBodyVariables = useCallback(
    (index) => {
      let nextBodyText = '';
      setFormData((prev) => {
        let bodyObj = prev.components?.find((c) => c.type === 'BODY');
        const bodyVarArr = [...(bodyObj?.example?.body_text?.[0] || [])];
        const filteredVarArr = bodyVarArr.filter((_, i) => i !== index);
        let bodyText = bodyObj.text.replace(`{{${index + 1}}}`, '');
        nextBodyText = bodyText;
        bodyObj = {
          ...bodyObj,
          text: bodyText,
          example: filteredVarArr.length ? { body_text: [filteredVarArr] } : undefined,
        };
        if (!filteredVarArr.length) delete bodyObj.example;
        return {
          ...prev,
          param: filteredVarArr,
          components: prev.components.map((c) => (c.type === 'BODY' ? bodyObj : c)),
        };
      });
      syncBodyValidationError(nextBodyText);
    },
    [setFormData, syncBodyValidationError]
  );

  const handleChangeBodyVariables = useCallback(
    (value, index) => {
      setFormData((prev) => {
        const bodyObj = prev.components?.find((c) => c.type === 'BODY');
        const bodyVarArr = [...(bodyObj?.example?.body_text?.[0] || [])];
        bodyVarArr[index] = value;
        const updatedBody = {
          ...bodyObj,
          example: { ...bodyObj?.example, body_text: [bodyVarArr] },
        };
        return {
          ...prev,
          components: prev.components.map((c) => (c.type === 'BODY' ? updatedBody : c)),
        };
      });
      clearFieldError(`bodyVariable_${index}`);
    },
    [clearFieldError, setFormData]
  );

  const handleChangeBodyParams = useCallback(
    (value, index) => {
      setFormData((prev) => {
        const params = [...(prev.param || [])];
        params[index] = value;
        return { ...prev, param: params };
      });
      clearFieldError(`bodyParams_${index}`);
    },
    [clearFieldError, setFormData]
  );

  const handleChangeFooterData = useCallback(
    (text) => {
      setFormData((prev) => {
        const footerObj = prev.components?.find((c) => c.type === 'FOOTER');
        if (footerObj) {
          return {
            ...prev,
            components: prev.components.map((c) =>
              c.type === 'FOOTER' ? { ...c, text } : c
            ),
          };
        }
        return {
          ...prev,
          components: [...(prev.components || []), { type: 'FOOTER', text }],
        };
      });
    },
    [setFormData]
  );

  const handleChangeHeaderText = useCallback(
    (text) => {
      setFormData((prev) => ({
        ...prev,
        components: prev.components.map((c) =>
          c.type === 'HEADER' ? { ...c, text } : c
        ),
      }));
      clearFieldError('headerText');
    },
    [clearFieldError, setFormData]
  );

  const validateForm = useCallback(() => {
    const errors = validateTemplateForm(formData, buttonArrayObj);
    setFormErrors(errors);
    return !hasValidationErrors(errors);
  }, [buttonArrayObj, formData, setFormErrors]);

  const submit = useCallback(async () => {
    const nextContextErrors = {};
    if (isSuperuser && !isEditMode && !filterObj?.organisationObj) {
      nextContextErrors.organisation = 'Organisation is required';
    }
    if (!filterObj?.botNumber) {
      nextContextErrors.botNumber = 'Bot number is required';
    }
    if (Object.keys(nextContextErrors).length > 0) {
      setContextErrors(nextContextErrors);
      showSnackBar('Please select organisation and bot number', 'error');
      return { success: false };
    }

    if (!validateForm()) return { success: false };

    if (!isEditMode && isCheckingTemplateName) {
      showSnackBar('Checking template name availability...', 'info');
      return { success: false };
    }

    if (!isEditMode && filterObj?.botNumber && formData?.name?.trim()) {
      try {
        const availability = await templateService.checkTemplateNameAvailability(
          formData.name.trim(),
          filterObj.botNumber
        );
        if (!availability.available) {
          const message = availability.message || TEMPLATE_NAME_TAKEN_MESSAGE;
          setFormErrors((prev) => ({ ...prev, name: message }));
          showSnackBar(message, 'error');
          return { success: false };
        }
      } catch (error) {
        showSnackBar(parseApiError(error, 'Unable to verify template name'), 'error');
        return { success: false };
      }
    }

    setIsSubmitting(true);
    let payload = buildCreateTemplatePayload(formData, buttonArrayObj);
    payload.tag_ids = selectedTags.map((tag) => tag.id).filter(Boolean);

    const isTrustSignalBot = filterObj.botNumberObj?.source === 'trustsignal';
    if (isTrustSignalBot) {
      payload.shortlink = `${formData.shortlink ?? '1'}`.trim() || '1';
    } else {
      delete payload.shortlink;
    }
    const headerObjIndex = payload.components?.findIndex((c) => c.type === 'HEADER');

    try {
      if (
        headerObjIndex !== -1 &&
        payload.components[headerObjIndex]?.format !== 'TEXT' &&
        file
      ) {
        const headerFileFormData = new FormData();
        headerFileFormData.append('file', file);
        const fileResponse = await templateService.uploadHeaderMedia(
          headerFileFormData,
          filterObj.botNumber
        );
        if (fileResponse?.status === 200) {
          const uploadData = fileResponse?.data?.data ?? {};
          const headerUrl = uploadData?.s3_object?.url ?? '';
          if (!headerUrl) {
            showSnackBar(
              parseApiError(fileResponse?.data, 'Header file URL was not returned. Please retry upload.'),
              'error'
            );
            return { success: false };
          }
          const sessionId = uploadData?.id;
          const media = { header: headerUrl };
          const components = [...payload.components];
          const headerComponent = { ...components[headerObjIndex] };
          delete headerComponent.text;
          if (sessionId) {
            headerComponent.example = { header_handle: [sessionId] };
          } else if (headerComponent.example?.header_handle) {
            const nextExample = { ...headerComponent.example };
            delete nextExample.header_handle;
            if (Object.keys(nextExample).length) {
              headerComponent.example = nextExample;
            } else {
              delete headerComponent.example;
            }
          }
          components[headerObjIndex] = headerComponent;
          payload = { ...payload, components, media };
          setFormData((prev) => ({ ...prev, components, media }));
        } else {
          showSnackBar(
            parseApiError(fileResponse?.data ?? fileResponse, 'Error when uploading the header file'),
            'error'
          );
          return { success: false };
        }
      }

      const res = isEditMode
        ? await templateService.updateTemplate({ ...payload, bot_number: filterObj.botNumber })
        : await templateService.createTemplate(payload, filterObj.botNumber);

      if (res?.status === 200 && res?.data?.status !== false) {
        showSnackBar(
          isEditMode ? 'Template Updated Successfully' : 'Template Created Successfully',
          'success'
        );
        setListRefresh((n) => n + 1);
        navigate(`${TEMPLATE_BASE_PATH}/template-list`);
        return { success: true };
      }

      showSnackBar(
        parseApiError(res?.data ?? res, `Failed to ${isEditMode ? 'Update' : 'Create'} Template`),
        'error'
      );
      return { success: false };
    } catch (error) {
      showSnackBar(
        parseApiError(error, `Failed to ${isEditMode ? 'Update' : 'Create'} Template`),
        'error'
      );
      return { success: false };
    } finally {
      setIsSubmitting(false);
    }
  }, [
    buttonArrayObj,
    file,
    filterObj.botNumber,
    filterObj.organisationObj,
    formData,
    formData.name,
    isCheckingTemplateName,
    isEditMode,
    isSuperuser,
    navigate,
    selectedTags,
    setFormData,
    setFormErrors,
    setListRefresh,
    showSnackBar,
    validateForm,
  ]);

  const selectedHeaderFormat =
    HeaderOptions.find((option) => getHeaderType(option.value))?.value || '';

  return {
    isEditMode,
    isCloneMode,
    cloneSourceName,
    formData,
    setFormData,
    buttonArrayObj,
    setButtonArrayObj,
    formErrors,
    setFormErrors,
    clearFieldError,
    isSubmitting,
    isCheckingTemplateName,
    file,
    setFile,
    existingFileUrl,
    bodyEditorRef,
    filterObj,
    contextErrors,
    botOptionsCount,
    isSuperuser,
    selectedHeaderFormat,
    getHeaderType,
    getHeaderText,
    getHeaderAcceptValue,
    handleChangeName,
    handleChangeDescription,
    onSelectHeaderType,
    onFileSelect,
    onFileRemove,
    handleChangeBodyData,
    addBodyVariables,
    removeBodyVariables,
    handleChangeBodyVariables,
    handleChangeBodyParams,
    handleChangeFooterData,
    handleChangeHeaderText,
    handleOrganisationChange,
    handleBotChange,
    selectedTags,
    setSelectedTags,
    companyId:
      filterObj?.organisationObj?.organisation_id ??
      filterObj?.organisation ??
      loggedInUser?.profile?.organisation?.organisation_id ??
      null,
    submit,
    resetButtons: () => setButtonArrayObj({ ...DEFAULT_TEMPLATE_BUTTONS }),
  };
}
